import { GetProductsContentResponse, ProductsCmsResponse } from 'store/queries/cms-api/types'
import { ProductContent } from 'types/products'
import { store } from 'store/index'
import { UserTypes } from 'store/auth/types'
import { Products } from 'features/contracts/add/types'
import {
  CONTACT_MODES,
  ContactMode,
  EL_PRODUCT_CODES,
  ELProduct,
  GAS_PRODUCT_CODES,
  getProductFromProductCode,
  PLENTY_PRODUCTS,
  PriceList,
  Product,
  PRODUCT_BILLING_OPTIONS,
  PRODUCT_CONFIG_OPTIONS,
  PRODUCT_CONTACT_OPTIONS,
  PRODUCT_TYPES,
  PRODUCTS,
  PRODUCTS_CONFIG,
  Region,
  REGIONS,
  ServiceContract,
  SIMULATION_TYPES,
  SimulationType
} from '@boltenergy-be/shared'

/**
 * Helper function that converts the cms response into a dictionary
 * @param {ProductsCmsResponse} products
 * @returns {GetProductsContentResponse}
 */
export const convertProductsCmsResponseToDictionary = (products: ProductsCmsResponse): GetProductsContentResponse => {
  const normalizedProducts = products
    .map((product) => ({
      ...product,
      slug: product?.apiProduct?.slug as Product
    }))
    .filter((product) => product.slug)

  if (!normalizedProducts.length) return null

  return normalizedProducts.reduce((acc: { [p in Product]?: ProductContent }, product) => {
    acc[product.slug] = product

    return acc
  }, {})
}

/**
 * Helper function that checks if a product is digital only
 * @param {Product} product
 * @returns {{[PRODUCT_CONFIG_OPTIONS.billingOptions]: boolean, [PRODUCT_CONFIG_OPTIONS.contactOptions]: boolean}}
 */
export const checkIfProductIsDigitalOnly = (
  product: Product
): { [PRODUCT_CONFIG_OPTIONS.billingOptions]: boolean; [PRODUCT_CONFIG_OPTIONS.contactOptions]: boolean } => {
  const contactOptions = PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.contactOptions]
  const billingOptions = PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.billingOptions]

  return {
    [PRODUCT_CONFIG_OPTIONS.contactOptions]: contactOptions.length === 1 && contactOptions[0] === PRODUCT_CONTACT_OPTIONS.digital,
    [PRODUCT_CONFIG_OPTIONS.billingOptions]: billingOptions.length === 1 && billingOptions[0] === PRODUCT_BILLING_OPTIONS.digital
  }
}

/**
 * Helper function that returns the default products for a given region
 * @param {{
 *   availableProducts?: Product[]
 *   isEarlyBird?: boolean
 *   region: Region
 *   simulationType?: SimulationType
 *   userType: UserTypes | null
 *   withBoltGo?: boolean
 * }} payload
 * @returns {Product[]}
 */
export const getUsableProducts = ({
  availableProducts = undefined,
  isEarlyBird = false,
  region,
  simulationType = undefined,
  userType = null,
  withBoltGo = false,
  plentyProducts = false
}: {
  availableProducts?: Product[]
  isEarlyBird?: boolean
  region: Region
  simulationType?: SimulationType
  userType: UserTypes | null
  withBoltGo?: boolean
  plentyProducts?: boolean
}): Product[] => {
  // Override
  const ALLOW_FIX_EVERYWHERE = true

  // Define some constants
  const isSalesUser = userType === UserTypes.SALES
  const isSuperUser = userType === UserTypes.SUPER_USER

  // Always skip early bird products, they are replaced in the .reduce below
  const productsToSkip: Product[] = [PRODUCTS.earlyBirdOffline, PRODUCTS.earlyBirdOnline]

  // Add Bolt GO to skippable products
  if (!withBoltGo) productsToSkip.push(PRODUCTS.go)

  if (simulationType === SIMULATION_TYPES.boltGo) {
    productsToSkip.push(...[PRODUCTS.fix, PRODUCTS.variableOffline, PRODUCTS.variableOnline])
  }

  const hasPlentyInAvailableProducts = !!availableProducts?.some((product) => PLENTY_PRODUCTS.includes(product))
  const simulateWithPlenty = plentyProducts || hasPlentyInAvailableProducts
  if (!simulateWithPlenty) {
    // Filter out Plenty products if not simulating with Plenty
    productsToSkip.push(...PLENTY_PRODUCTS)
  }

  if (plentyProducts) {
    // If the contact has Plenty products, we need to skip the counterparts of Plenty products
    productsToSkip.push(...PLENTY_PRODUCTS.map((p) => PRODUCTS_CONFIG[p][PRODUCT_CONFIG_OPTIONS.counterpart]).filter((p) => !!p))
  }

  // Loop over products in the config and return the ones that are available in the given region
  return Object.keys(PRODUCTS_CONFIG).reduce<Product[]>((productsForGivenRegion, product) => {
    const productConfig = PRODUCTS_CONFIG[product as Product]

    const availableIn =
      ALLOW_FIX_EVERYWHERE && productConfig.type === PRODUCT_TYPES.fix
        ? ([REGIONS.flanders, REGIONS.wallonia, REGIONS.brussels] as Region[])
        : productConfig[PRODUCT_CONFIG_OPTIONS.availableInRegions]

    // Skip product if it's in the skippable list
    if (productsToSkip.includes(product as Product)) return productsForGivenRegion

    // Skip the product for sales user if it's not in the available products list in a non-BOLT_GO simulation
    if (simulationType !== SIMULATION_TYPES.boltGo && availableProducts && !availableProducts.includes(product as Product)) {
      return productsForGivenRegion
    }

    // Check if product is in previous contracts
    const { billingContracts } = store.getState().contact
    const isInPreviousContracts = Object.values(billingContracts).some(
      (contract) =>
        contract.serviceContracts.electricity.detail.productCode ===
        productConfig[PRODUCT_CONFIG_OPTIONS.productCodes][contract.serviceContracts.electricity.detail.energyType]
    )

    // Add product if it's available in the given region
    if (availableIn.includes(region) || (isSuperUser && isInPreviousContracts)) {
      const shouldChangeToEarlyBird =
        !isSalesUser &&
        isEarlyBird &&
        !simulateWithPlenty &&
        ([PRODUCTS.variableOffline, PRODUCTS.variableOnline] as Product[]).includes(product as Product)

      productsForGivenRegion.push(shouldChangeToEarlyBird ? convertProductToEarlyBird(product as Product) : (product as Product))
    }

    return productsForGivenRegion
  }, [])
}

/**
 * Helper function that converts a variable product to early bird product
 * @param {Product} product
 * @returns {Product}
 */
const convertProductToEarlyBird = (product: Product): Product => {
  switch (product) {
    case PRODUCTS.variableOffline:
      return PRODUCTS.earlyBirdOffline
    case PRODUCTS.variableOnline:
      return PRODUCTS.earlyBirdOnline
    default:
      return product
  }
}

/**
 * Checks if at least one contract has one of the given products
 * @param {ELProduct[]} products
 * @returns {boolean}
 */
export const checkHasProduct = (products: ELProduct[]): boolean => {
  const { userType } = store.getState().auth
  if (!userType || userType === UserTypes.SALES) return false

  const { billingContracts } = store.getState().contact

  return Object.values(billingContracts).some((contract) => {
    const productCode = contract.serviceContracts.electricity.detail.productCode
    return products.some((p) => p === productCode)
  })
}

/**
 * Checks if at least one contract has early bird product
 * @returns {boolean}
 */
export const checkIsEarlyBird = (): boolean => checkHasProduct([EL_PRODUCT_CODES.EarlyBirdOn, EL_PRODUCT_CODES.EarlyBirdOff])

/**
 * Checks if the selected customer has a fixed product
 * @returns {boolean}
 */
export const checkHasFixedProduct = (contract: ServiceContract): boolean => {
  const selectedCustomerProduct = getProductFromProductCode(contract?.detail?.productCode)
  return checkIsFixProduct(selectedCustomerProduct)
}

/**
 * Checks if the given product is a fixed product
 * @param {Product | undefined} product
 * @returns {boolean}
 */
export const checkIsFixProduct = (product?: Product): boolean => {
  if (!product) return false

  return PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.type] === PRODUCT_TYPES.fix
}

/**
 * Defines products:
 * If at least one contract has early bird product: re-use early bird
 * Otherwise check based on delivery mode
 *
 * @param {ContactMode} contactMode
 * @param {boolean} needsGas
 * @param {boolean} hasEarlyBirdChecked
 * @returns {Products}
 */
export const defineProducts = (contactMode: ContactMode, needsGas: boolean, hasEarlyBirdChecked?: boolean): Products => {
  const hasEarlyBird = hasEarlyBirdChecked || checkIsEarlyBird()

  if (contactMode === CONTACT_MODES.email) {
    return {
      electricity: hasEarlyBird ? EL_PRODUCT_CODES.EarlyBirdOn : EL_PRODUCT_CODES.VariableOn,
      ...(needsGas && {
        gas: hasEarlyBird ? GAS_PRODUCT_CODES.EarlyBirdOn : GAS_PRODUCT_CODES.VariableOn
      })
    }
  }

  return {
    electricity: hasEarlyBird ? EL_PRODUCT_CODES.EarlyBirdOff : EL_PRODUCT_CODES.VariableOff,
    ...(needsGas && {
      gas: hasEarlyBird ? GAS_PRODUCT_CODES.EarlyBirdOff : GAS_PRODUCT_CODES.VariableOff
    })
  }
}

/**
 * Extract the version number from a pricelist url
 * @param {string} url
 * @returns {number | null}
 * @example
 * extractPricelistVersion("https://files.boltenergie.be/pricelists/var/bolt_res_el_nl_11.pdf")
 * // => 11
 */
export const extractPricelistVersion = (url: string): number | null => {
  const match = url.match(/_(\d+)(?:_(\d+))?\.pdf$/)
  if (!match) return null
  return match[2] ? parseFloat(`${match[1]}.${match[2]}`) : parseInt(match[1], 10)
}

/**
 * Checks if the product is renewable based on the current and new pricelist and product
 * @param {{newPriceList: PriceList | undefined, currentPriceList: PriceList | undefined, currentProduct: Product | undefined, newProduct: Product | undefined}} param0
 * @param {PriceList | undefined} param0.newPriceList
 * @param {Product | undefined} param0.currentProduct
 * @param {PriceList | undefined} param0.currentPriceList
 * @param {Product | undefined} param0.newProduct
 * @returns {boolean}
 */
export const checkProductIsRenewable = ({
  currentProduct,
  currentPriceList,
  newPriceList,
  newProduct
}: {
  currentPriceList: PriceList | undefined
  currentProduct: Product | undefined
  newPriceList: string
  newProduct: Product | undefined
}): boolean => {
  if (!newPriceList || !currentPriceList?.url || !currentProduct || !newProduct || newProduct !== currentProduct) {
    return false
  }

  const currentPriceListVersion = extractPricelistVersion(currentPriceList.url)
  const newPriceListVersion = extractPricelistVersion(newPriceList)

  if (!currentPriceListVersion || !newPriceListVersion) {
    return false
  }

  return currentPriceListVersion !== newPriceListVersion
}
